import React from 'react';
import styled, { css } from 'styled-components';
import { media, boxShadows, colors } from '../../../../../shared/lib/styles';
import { publicImageUrl } from '../../../../lib/publicImageUrl';
import { CheckList } from '../../../../../shared/components/List';
import { Figure } from '../../../../../shared/components/Image';
import Heading from '../../../../../shared/components/Heading';
import Paragraph from '../../../../../shared/components/Paragraph';
import DisplayName from '../../../../../shared/components/DisplayName';
import BoxWithGradient from '../../../../../shared/components/BoxWithGradient';
import getHighContrastTextColor from '../../../../../shared/lib/getHighContrastTextColor';

const StyledShowcase = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    height: 100%;

    @media ${media('large')} {
        padding: 50px 0 50px 40px;
        height: 550px;
    }

    @media ${media('normal')} {
        height: 550px;
    }

    @media ${media('small', 'medium')} {
        flex-direction: column;
    }
`;

const ImageWrapper = styled.div`
    z-index: 2;
    box-shadow: ${boxShadows.high};

    @media ${media('small')} {
        box-shadow: ${boxShadows.medium};
    }

    img {
        @media ${media('large')} {
            max-width: none;
        }
    }
`;

const ShowcaseDetails = styled.div<{ featureColor: string }>(({ featureColor }) => {
    return css`
        min-width: 50%;
        width: 300px;
        z-index: 1;
        padding: 20px;
        color: ${getHighContrastTextColor(featureColor)};

        @media ${media('large')} {
            width: 370px;
            padding-left: 30px;
            padding-right: 70px;
        }

        @media ${media('medium')} {
            margin-right: -60px;
        }

        @media ${media('small', 'medium')} {
            margin-top: -40px;
            width: 100%;
            background-color: ${featureColor};
            padding: 70px 40px 40px 40px;
        }

        @media ${media('small')} {
            margin-right: -30px;
        }
    `;
});

const BackgroundSquare = styled.div<{ featureColor: string }>`
    min-width: 450px;
    background-color: ${({ featureColor }) => featureColor};
    position: absolute;

    @media ${media('large')} {
        width: 550px;
    }

    @media ${media('normal')} {
        width: 550px;
    }

    @media ${media('small', 'medium')} {
        display: none;
    }

    &:after {
        content: '';
        display: block;
        padding-bottom: 100%;
    }
`;

interface Props {
    designerImage: DesignerImage;
    showcaseDetail: ShowcaseDetail;
}

export type DesignerImage = {
    imageUrl: string;
    imageCaption: string;
    alt: string;
    featureColor: string;
};

export type ShowcaseDetail = {
    title: string;
    copy: string;
    features: string[];
};

function Showcase({ designerImage, showcaseDetail }: Props) {
    return (
        <StyledShowcase>
            <ImageWrapper>
                <Figure
                    src={publicImageUrl(designerImage.imageUrl)}
                    width={450}
                    height={450}
                    alt={designerImage.alt}
                    caption={
                        <BoxWithGradient padding="space7">
                            <DisplayName style={{ color: colors.white, fontSize: '14px' }}>
                                {designerImage.imageCaption}
                            </DisplayName>
                        </BoxWithGradient>
                    }
                />
            </ImageWrapper>
            <ShowcaseDetails featureColor={designerImage.featureColor}>
                <Heading type={'h3'} variations={['h5']}>
                    {showcaseDetail.title}
                </Heading>
                <Paragraph>{showcaseDetail.copy}</Paragraph>
                <CheckList items={showcaseDetail.features} />
            </ShowcaseDetails>
            <BackgroundSquare featureColor={designerImage.featureColor} />
        </StyledShowcase>
    );
}

export default Showcase;
